import { AES, enc } from 'crypto-js';

//NOTE: this is only intended to deter casual inspection of the local storage
export const ENCRYPTION_KEY = 'f81d4fae-7dec-11d0-a765-00a0c91e6bf6';

const toDataFormat = <T>(data: T): string => JSON.stringify({ data });
const fromDataFormat = <T>(data: string): T => JSON.parse(data).data;

//NOTE: Not to be used for NPI data
export const encryptData = (data: string, encryptKey: string): string | null => {
  try {
    const encodedData = AES.encrypt(data, encryptKey).toString();
    return encodedData ?? null;
  } catch (error) {
    console.error(error.message);
    return null;
  }
};

//NOTE: Not to be used for NPI data
export const decryptData = (data: string, encryptKey: string): string | null => {
  try {
    const bytes = AES.decrypt(data, encryptKey);
    const decryptedData = bytes.toString(enc.Utf8);

    if (decryptedData.length === 0) {
      throw new Error('Decryption failed');
    }
    return decryptedData;
  } catch (error) {
    console.error(error.message);
    return null;
  }
};

export type UseLocalStorageType = {
  get: <T>(key: string) => T | null;
  set: <T>(key: string, value: T) => void;
  getSecure: <T>(key: string) => T | null;
  setSecure: <T>(key: string, value: T) => void;
  remove: (key: string) => void;
};

export const useLocalStorage = (): UseLocalStorageType => {
  const get = <T>(key: string): T | null => {
    try {
      const data = localStorage.getItem(key) as string;
      if (!data) return null;
      return fromDataFormat(data);
    } catch (error) {
      console.error(error.message);
      return null;
    }
  };

  const set = <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, toDataFormat(value));
    } catch (error) {
      console.error(error.message);
    }
  };

  const getSecure = <T>(key: string): T | null => {
    const value = localStorage.getItem(key);
    if (value) {
      const decryptedValue = decryptData(value, ENCRYPTION_KEY);
      if (!decryptedValue) return null;
      return fromDataFormat(decryptedValue) as T;
    }
    return null;
  };

  const setSecure = <T>(key: string, value: T): void => {
    try {
      const encryptedValue = encryptData(toDataFormat(value), ENCRYPTION_KEY);
      if (encryptedValue) {
        localStorage.setItem(key, encryptedValue);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  const remove = (key: string): void => {
    localStorage.removeItem(key);
  };
  return { get, set, getSecure, setSecure, remove };
};
